import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { getBackgroundColor } from './resultUtils';
import { ArrowBack } from '@mui/icons-material';
import { sentenceFactory } from './models/sentenceFactory';
import ConversationList from './ConversationList';
import _ from 'lodash';
import SentenceStats from './SentenceStats';
import { FaEllipsisV } from 'react-icons/fa';
import { useAutoPlay } from './contexts/AutoPlayContext';
import { BasicStates } from './fsm/AutoPlayBaseFSM';

// --- Color background by average score ---
const getScoreColor = (score) => {
  if (score == null) return '#f5f5f5';
  if (score < 40) return '#ffcccc';
  if (score < 70) return '#fff3cd';
  return '#d4edda';
};

const SentenceRow = React.memo(
  React.forwardRef(function SentenceRow(
    {
      idx,
      convLine,
      sentence,
      name,
      alignRight,
      isSelected,
      onSentenceClick,
      onShowDetails,
      getBackgroundColor,
    },
    ref
  ) {
    const handleClick = React.useCallback(() => {
      onSentenceClick(idx);
    }, [onSentenceClick, idx]);

    const handleShowDetails = React.useCallback(
      (e) => {
        e.stopPropagation();
        if (onShowDetails) {
          onShowDetails();
        }
      },
      [onShowDetails]
    );

    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: alignRight ? 'flex-end' : 'flex-start',
        }}
      >
        <Paper
          onClick={handleClick}
          sx={{
            p: 1.5,
            maxWidth: '70%',
            backgroundColor: getBackgroundColor(sentence) ?? '#f5f5f5',
            backgroundImage:
              'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.05) 100%)',
            borderRadius: alignRight
              ? '16px 16px 4px 16px'
              : '16px 16px 16px 4px',
            boxShadow: 1,
            cursor: 'pointer',
            position: 'relative',
            ...(alignRight ? { ml: 'auto' } : { mr: 'auto' }),
            ...(isSelected && {
              border: '2px solid',
              borderColor: 'primary.main',
              boxShadow: 3,
            }),
          }}
        >
          {/* Top row: name on left, details icon on right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 0.5,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: alignRight ? 'primary.dark' : 'secondary.dark',
                opacity: 0.8,
              }}
            >
              {`${name}:`}
            </Typography>

            <IconButton
              size="small"
              tabIndex={0}
              aria-label="Show details"
              onClick={handleShowDetails}
              sx={{ cursor: 'pointer', p: 0.5, ml: 1 }}
            >
              <FaEllipsisV color="gray" />
            </IconButton>
          </Box>

          {/* Main sentence */}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              lineHeight: 1.5,
              color: '#212121',
              fontFamily: 'Open Sans, Roboto, sans-serif',
            }}
          >
            {sentence?.sentence ?? `[missing phrase ${convLine.phraseId}]`}
          </Typography>

          {/* Translation below */}
          <Typography
            variant="body2"
            sx={{
              mt: 0.25,
              fontStyle: 'italic',
              color: 'text.secondary',
              opacity: 0.8,
            }}
          >
            {sentence?.translation ?? ''}
          </Typography>
        </Paper>
      </Box>
    );
  })
);

const Conversations = ({
  content,
  sentenceCallBack,
  autoPlayState,
  errorCallBack,
  allContentTags,
  onConversationSelectionChange,
  timingMode = 'fixedDelay',
  fixedDelaySeconds = 1.0,
  autoplayPlaybackRate = 1.0,
}) => {
  const emptyConversationListSearchState = React.useMemo(() => ({
    searchTerm: ['', ''],
    showSearchInput: [false, false],
  }), []);
  const itemRefs = React.useRef({});

  const [sentenceForStats, setSentenceForStats] = useState(null);
  const [isSentenceStatsVisible, setIsSentenceStatsVisible] = useState(false);

  const [manualClick, setManualClick] = useState(false);

  const ctx = useAutoPlay();

  // Build a map from phraseId -> sentence index using precomputed content.sentences
  const indexMap = React.useMemo(() => {
    console.time('convIndexMap');
    const map = {};
    content?.sentences?.forEach((sentence, i) => {
      const id = sentence?.ID;
      if (id != null) map[id] = i;
    });
    console.timeEnd('convIndexMap');
    return map;
  }, [content?.sentences]);

  const sentenceById = React.useMemo(() => {
    const map = {};
    content?.sentences?.forEach((sentence) => {
      const id = sentence?.ID;
      if (id != null) map[id] = sentence;
    });
    return map;
  }, [content?.sentences]);

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [lastSelectedConversationId, setLastSelectedConversationId] = useState(null);
  const [selectedSentenceIndex, setSelectedSentenceIndex] = useState(null);
  const [conversationListSearchState, setConversationListSearchState] = useState(
    emptyConversationListSearchState
  );

  const disableConversationContainer = (autoPlayState) => ({
    opacity: autoPlayState !== BasicStates.IDLE ? 0.5 : 1,
    pointerEvents: autoPlayState !== BasicStates.IDLE ? 'none' : 'auto',
    transition: 'opacity 0.2s',
  });

  useEffect(() => {
    setSelectedConversation(null);
    setLastSelectedConversationId(null);
    setSelectedSentenceIndex(null);
    setConversationListSearchState(emptyConversationListSearchState);
  }, [
    content?.sentences?.length,
    content?.content,
    content?.Learn,
    content?.Base,
    emptyConversationListSearchState,
  ]);

  // stabilize click handler so child memoization works
  const handleSentenceClick = React.useCallback(
    (idx) => {
      if (idx >= 0) {
        setSelectedSentenceIndex(idx);
        setManualClick(true);


        // Only trigger immediate playback when autoplay is idle.
        if (ctx.autoPlayState === BasicStates.IDLE) {
          const convLine = selectedConversation?.sentences[idx];
          const id = convLine?.phraseId ?? null;
          if (id) {
            const originalIdx = indexMap[id];
            const newSentence = sentenceFactory(content, originalIdx, {
              dontUpdateContent: true,
            });
            // Manual play from the Conversations view
            sentenceCallBack(newSentence, 'conversation');
          }
        }
      }
    },
    [selectedConversation, indexMap, content, sentenceCallBack, ctx.autoPlayState]
  );

  // autoplay: play currently selected sentence
  const playSelectedSentence = React.useCallback(() => {
    if (!selectedConversation || selectedSentenceIndex == null) return;
    const convLine = selectedConversation.sentences[selectedSentenceIndex];
    const id = convLine?.phraseId ?? null;
    if (!id) return;
    const originalIdx = indexMap[id];
    if (typeof originalIdx !== 'number') return;
    const newSentence = sentenceFactory(content, originalIdx, {
      dontUpdateContent: true,
    });
    newSentence.timingMode = timingMode;
    newSentence.fixedDelaySeconds = fixedDelaySeconds;
    newSentence.autoplayPlaybackRate = autoplayPlaybackRate;
    sentenceCallBack(newSentence, 'conversation');
  }, [
    selectedConversation,
    selectedSentenceIndex,
    indexMap,
    content,
    sentenceCallBack,
    timingMode,
    fixedDelaySeconds,
    autoplayPlaybackRate,
  ]);

  // autoplay: advance to next sentence within the selected conversation
  const advanceSelectedSentence = React.useCallback(() => {
    if (!selectedConversation || !Array.isArray(selectedConversation.sentences)) {
      ctx.dispatch({ type: 'stop' });
      return;
    }
    
    if (selectedSentenceIndex == null) {
      // start at first line
      if (selectedConversation.sentences.length > 0) {
        setSelectedSentenceIndex(0);
      } else {
        ctx.dispatch({ type: 'stop' });
      }
      setManualClick(false);
      return;
    }

    const nextIndex = selectedSentenceIndex + 1;
    if (nextIndex < selectedConversation.sentences.length) {
      setSelectedSentenceIndex(nextIndex);
      setManualClick(false);
      return;
    }

    // reached end of conversation, stop autoplay
    ctx.dispatch({ type: 'stop' });
    setManualClick(false);
  }, [selectedConversation, selectedSentenceIndex, ctx]);

  // respond to global autoplay tokens
  useEffect(() => {
    if (ctx.autoPlayState !== BasicStates.IDLE) {
      advanceSelectedSentence();
    }
  }, [ctx.ui.contentAdvanceRowToken]);

  useEffect(() => {
    if (ctx.autoPlayState !== BasicStates.IDLE) {
      playSelectedSentence();
    }
  }, [ctx.ui.contentPlayRowToken]);

  useEffect(() => {
  // When autoplay starts
    if (ctx.autoPlayState === BasicStates.PLAYING && selectedConversation) {
      
      const lastIndex = selectedConversation.sentences.length - 1;

      // CASE 1: Autoplay pressed at the end → restart from beginning
      if (selectedSentenceIndex === lastIndex) {
        // setSelectedSentenceIndex(0);

        // requestAnimationFrame(() => {
            setSelectedSentenceIndex(null);

        // });

        return;  // Wait for FSM to begin playing from index 0
      }

      // CASE 2: Autoplay pressed with a selected sentence → continue from that line
      if (selectedSentenceIndex != null) {
        advanceSelectedSentence();
        return;
      }

      // CASE 3: No line selected → start from beginning
      if (selectedConversation.sentences.length > 0) {
        setSelectedSentenceIndex(0);
      }
    }
  }, [ctx.autoPlayState]);


  // scroll to selected sentence when it changes
  useEffect(() => {
    if (selectedSentenceIndex == null) return;
    const r = itemRefs.current[selectedSentenceIndex];
    if (r && r.current && typeof r.current.scrollIntoView === 'function') {
      r.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedSentenceIndex]);

  const conversations = content?.conversations ?? [];

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setLastSelectedConversationId(conv?._id ?? null);
    setSelectedSentenceIndex(null);

    if (onConversationSelectionChange) {
      onConversationSelectionChange(true); // a conversation is now open
    }

  };

  const handleBack = () => {
    setSelectedConversation(null);
    setSelectedSentenceIndex(null);

    if (onConversationSelectionChange) {
      onConversationSelectionChange(false); // back to list view
    }
  };

  if (!content || !Array.isArray(content.sentences)) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          No conversation content loaded.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflowY: 'hidden', // parent should not scroll
        WebkitOverflowScrolling: 'touch',
        ...disableConversationContainer(ctx.autoPlayState),
      }}
    >
      <SentenceStats
        sentence={sentenceForStats}
        isOpen={isSentenceStatsVisible}
        isRtlDict={{}}
        sentenceCallBack={sentenceCallBack}
        onClose={() => setIsSentenceStatsVisible(false)}
        errorCallBack={errorCallBack}
        allContentTags={allContentTags}
      />
      {/* --- When no conversation is selected: show picker --- */}
      {(!selectedConversation || _.isEmpty(selectedConversation)) && (
        <ConversationList
          content = {content}
          onSelect={handleSelectConversation}
          sentenceById={sentenceById}
          learnLabel={content?.Learn}
          baseLabel={content?.Base}
          searchState={conversationListSearchState}
          onSearchStateChange={setConversationListSearchState}
          selectedConversationId={lastSelectedConversationId}
        />
      )}

      {/* --- When conversation is selected: show dialogue only --- */}
      {selectedConversation && (
        <Paper
          sx={{
            p: 1,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            WebkitOverflowScrolling: 'touch',
            ...disableConversationContainer(ctx.autoPlayState),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              flexWrap: 'wrap',
              gap: 1,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
              py: 0.5,
            }}
          >
            {/* Left: back button + title/level */}
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <IconButton onClick={handleBack} sx={{ mr: 1 }}>
                <ArrowBack />
              </IconButton>
              <Box sx = {{display:'flex',  alignItems: 'center',}}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {selectedConversation.title} 
                </Typography>
                <Typography variant="subtitle" color="text.secondary" sx = {{ml:1}}>
                  ({selectedConversation.Level})
                </Typography>
              </Box>
            </Box> {/* <-- Added missing closing tag here */}
            
          </Box>

          <List
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              px: 1,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {selectedConversation.sentences.map((convLine, idx) => {
              if (!itemRefs.current[idx]) itemRefs.current[idx] = React.createRef();

              const roleObj = selectedConversation.participants?.[convLine.role];
              const name = roleObj?.name ?? convLine.role;
              const alignRight = convLine.role === 'B';

              const isSelected = idx === selectedSentenceIndex;
              const ID = convLine.phraseId;
              const contentIndex = indexMap[ID];
              const sentence =
                typeof contentIndex === 'number'
                  ? content.sentences?.[contentIndex] ??
                    sentenceFactory(content, contentIndex, { dontUpdateContent: true })
                  : null;

              return (
                <ListItem
                  key={idx}
                  disablePadding
                  sx={{
                    display: 'block', // ensures it doesn’t constrain children horizontally
                    width: '100%', // make sure it spans full container width
                  }}
                  ref={itemRefs.current[idx]}
                >
                  <SentenceRow
                    idx={idx}
                    convLine={convLine}
                    sentence={sentence}
                    name={name}
                    alignRight={alignRight}
                    isSelected={isSelected}
                    onSentenceClick={handleSentenceClick}
                    onShowDetails={() => {
                      setSentenceForStats(
                        typeof contentIndex === 'number'
                          ? sentenceFactory(content, contentIndex, { dontUpdateContent: true })
                          : null
                      );
                      setIsSentenceStatsVisible(true);
                    }}
                    getBackgroundColor={getBackgroundColor}
                  />
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default Conversations;
